import { Ban, Check, MoreHorizontal, Plus, UserX } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';

import { ConfirmDialog } from '@/components/confirm-dialog';
import { MemberDialog, type MemberValues } from '@/components/member-dialog';
import { NotificationChannelDialog } from '@/components/notification-channel-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSettings, useUsers } from '@/lib/api-hooks';
import { invalidateResource } from '@/lib/resource';
import {
  type ChannelInput,
  type Settings,
  updateNotificationChannel,
  updateSettings,
} from '@/lib/settings';
import {
  MEMBER_PAGE_SIZE,
  type Member,
  type MemberStatus,
  createMember,
  updateMember,
} from '@/lib/users';
import { cn } from '@/lib/utils';

export function SettingsPage() {
  const { data: settings, isLoading: loadingSettings } = useSettings();

  return (
    <div className="grid max-w-5xl gap-6 lg:grid-cols-2">
      {settings ? <PreferencesCard settings={settings} /> : null}
      <PlatformCard settings={settings} loading={loadingSettings} />

      <div className="lg:col-span-2">
        <TeamCard />
      </div>
    </div>
  );
}

function PreferencesCard({ settings }: { settings: Settings }) {
  const [days, setDays] = useState(String(settings.defaults.votingPeriodDays));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [channelsOpen, setChannelsOpen] = useState(false);

  const parsedDays = Number(days);
  const dirty = parsedDays > 0 && parsedDays !== settings.defaults.votingPeriodDays;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      await updateSettings({
        defaultVotingDays: parsedDays > 0 ? parsedDays : undefined,
      });
      invalidateResource('settings');
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  }

  async function handleChannelSubmit(type: string, input: ChannelInput) {
    await updateNotificationChannel(type, input);
    invalidateResource('settings');
  }

  return (
    <Card>
      <form className="flex flex-col gap-7" onSubmit={handleSubmit}>
        <CardHeader>
          <CardTitle>Preferences</CardTitle>
          <CardDescription>Defaults applied across the platform.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-6">
          <div className="flex items-center justify-between gap-4">
            <div className="grid gap-1">
              <Label htmlFor="voting-days">Default voting period</Label>
              <p className="text-muted-foreground text-sm">
                Applied when a scheduled voting period is created.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Input
                id="voting-days"
                type="number"
                min={1}
                max={3650}
                value={days}
                onChange={(event) => setDays(event.target.value)}
                className="w-20 text-right"
              />
              <span className="text-muted-foreground text-sm">days</span>
            </div>
          </div>

          <div className="flex items-center justify-between gap-4">
            <div className="grid gap-1">
              <Label>Notifications</Label>
              <p className="text-muted-foreground text-sm">Where admin alerts are delivered.</p>
            </div>
            <Button type="button" variant="outline" onClick={() => setChannelsOpen(true)}>
              Configure
            </Button>
          </div>
        </CardContent>
        <CardFooter className="justify-end gap-2 border-t">
          {saved && <span className="mr-auto text-muted-foreground text-sm">Saved</span>}
          <Button type="submit" disabled={!dirty || saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </CardFooter>
      </form>

      <NotificationChannelDialog
        channels={settings.notifications.channels}
        open={channelsOpen}
        onOpenChange={setChannelsOpen}
        onSubmit={handleChannelSubmit}
      />
    </Card>
  );
}

function PlatformCard({ settings, loading }: { settings?: Settings; loading: boolean }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Platform</CardTitle>
        <CardDescription>Runtime configuration for this deployment.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        {loading && !settings ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : settings ? (
          <>
            <Field label="Environment" value={settings.environment} />
            <Field label="Storage" value={settings.storage.provider} />
            <Field label="R2 bucket" value={settings.storage.bucket} />
            <Field label="CDN" value={settings.storage.publicUrl} />
            <div className="grid gap-1">
              <span className="text-muted-foreground text-sm">Sign-in providers</span>
              <div className="flex flex-wrap gap-1.5">
                {settings.oauthProviders.length > 0 ? (
                  settings.oauthProviders.map((provider) => (
                    <Badge key={provider} variant="outline" className="capitalize">
                      {provider}
                    </Badge>
                  ))
                ) : (
                  <span className="text-muted-foreground text-sm">None configured</span>
                )}
              </div>
            </div>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}

const STATUS_COPY: Record<
  MemberStatus,
  { title: string; description: string; label: string; variant: 'default' | 'destructive' }
> = {
  active: {
    title: 'Activate member?',
    description: 'They will be able to sign in again.',
    label: 'Activate',
    variant: 'default',
  },
  inactive: {
    title: 'Deactivate member?',
    description: 'They will not be able to sign in until reactivated.',
    label: 'Deactivate',
    variant: 'destructive',
  },
  banned: {
    title: 'Ban member?',
    description: 'They will be blocked from the CMS.',
    label: 'Ban',
    variant: 'destructive',
  },
};

function TeamCard() {
  const { data } = useUsers({ pageSize: MEMBER_PAGE_SIZE, role: 'admin' });
  const members = data?.items ?? [];
  const [dialogOpen, setDialogOpen] = useState(false);
  const [change, setChange] = useState<{ member: Member; status: MemberStatus } | null>(null);

  async function handleAdd(values: MemberValues) {
    await createMember({
      email: values.email,
      name: values.name || undefined,
      picture: values.picture ?? undefined,
    });
    invalidateResource('users');
  }

  async function applyStatus() {
    if (!change) return;
    await updateMember(change.member.id, { status: change.status });
    invalidateResource('users');
  }

  return (
    <Card>
      <CardHeader className="grid-cols-[1fr_auto] items-start gap-2">
        <div className="grid gap-1.5">
          <CardTitle>Team</CardTitle>
          <CardDescription>Who can sign in to the CMS.</CardDescription>
        </div>
        <Button size="sm" className="shrink-0" onClick={() => setDialogOpen(true)}>
          <Plus />
          Add member
        </Button>
      </CardHeader>
      <CardContent className="grid gap-1">
        {members.map((member) => (
          <div
            key={member.id}
            className="flex items-center justify-between gap-3 border-b py-3 last:border-0 last:pb-0"
          >
            <div className="flex min-w-0 items-center gap-3">
              <span className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted font-medium text-xs">
                {member.picture ? (
                  <img src={member.picture} alt="" className="h-full w-full object-cover" />
                ) : (
                  (member.name ?? member.email).slice(0, 1).toUpperCase()
                )}
              </span>
              <div className="min-w-0">
                <Link
                  to={`/members/${member.id}`}
                  className="block truncate font-medium text-sm hover:underline"
                >
                  {member.name ?? member.email}
                </Link>
                <p className="truncate text-muted-foreground text-xs">{member.email}</p>
              </div>
              {member.status !== 'active' && (
                <Badge
                  variant="outline"
                  className={cn(
                    'shrink-0 capitalize',
                    member.status === 'banned'
                      ? 'border-transparent bg-destructive/10 text-destructive'
                      : 'border-transparent bg-muted text-muted-foreground',
                  )}
                >
                  {member.status}
                </Badge>
              )}
            </div>

            <DropdownMenu modal={false}>
              <DropdownMenuTrigger
                aria-label={`Actions for ${member.name ?? member.email}`}
                className="inline-flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
              >
                <MoreHorizontal className="size-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {member.status !== 'active' && (
                  <DropdownMenuItem onSelect={() => setChange({ member, status: 'active' })}>
                    <Check />
                    Activate
                  </DropdownMenuItem>
                )}
                {member.status !== 'inactive' && (
                  <DropdownMenuItem onSelect={() => setChange({ member, status: 'inactive' })}>
                    <UserX />
                    Deactivate
                  </DropdownMenuItem>
                )}
                {member.status !== 'banned' && (
                  <DropdownMenuItem
                    variant="destructive"
                    onSelect={() => setChange({ member, status: 'banned' })}
                  >
                    <Ban />
                    Ban
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ))}
      </CardContent>

      <MemberDialog open={dialogOpen} onOpenChange={setDialogOpen} onSubmit={handleAdd} />

      {change && (
        <ConfirmDialog
          open
          onOpenChange={(next) => {
            if (!next) setChange(null);
          }}
          title={STATUS_COPY[change.status].title}
          description={`${change.member.name ?? change.member.email}: ${STATUS_COPY[change.status].description}`}
          confirmLabel={STATUS_COPY[change.status].label}
          confirmVariant={STATUS_COPY[change.status].variant}
          onConfirm={applyStatus}
        />
      )}
    </Card>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid gap-1">
      <span className="text-muted-foreground text-sm">{label}</span>
      <span className="font-medium text-sm">{value || 'Not set'}</span>
    </div>
  );
}
